import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { TCreateDeviceSchema } from '@/console/features/devices/components/create-device/create-device.types';
import {
  flattenCredentialMetaData,
  getCredentialInputKeys,
} from '@/console/features/devices/utils/credential-meta-data';
import { AUTONOMY_VPLC_ID_META_KEY } from '@/console/features/orchestrators/orchestrators.constants';
import { isSoftPlcDevice } from '@/console/features/orchestrators/utils/vplc-binding';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { useDefineAssetTags } from '@/shared/features/tags/hooks/use-define-asset-tags/use-define-asset-tags';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { useCreateDeviceMutation } from '@/shared/redux/api/assets/devices/devices-api';
import { useLinkDeviceToGatewayMutation } from '@/shared/redux/api/assets/links/links-api';
import { AssetTypeEnum } from '@/shared/types/types.generated';

export const useCreateDevice = (groupId?: string, handleClose?: () => void) => {
  const { t } = useTranslation();
  const [createDevice] = useCreateDeviceMutation();
  const [linkDevicesToGateway] = useLinkDeviceToGatewayMutation();
  const { getDeviceConfig } = useVendor();
  const [devicePort, setDevicePort] = useState<number>(0);
  const [boundIpAddress, setBoundIpAddress] = useState<string>();
  const defineAssetTags = useDefineAssetTags();

  const handleDeviceConfiguration = async (
    data: TSubmittedData<Partial<TCreateDeviceSchema>>,
  ) => {
    const deviceConfig = getDeviceConfig(data.data);

    if (deviceConfig) {
      setDevicePort(deviceConfig.port);
    }

    setBoundIpAddress(
      isSoftPlcDevice(data.data)
        ? data.data.softPlc?.ipAddress || undefined
        : undefined,
    );
  };

  const handleSubmit = async (data: TSubmittedData<TCreateDeviceSchema>[]) => {
    const deviceData = data[0].data;
    const connectionData = data[1].data;

    const {
      tags,
      vaultId: _,
      groupId: deviceGroupId,
      softPlc,
      ...deviceRest
    } = deviceData;
    const credentialKeys = getCredentialInputKeys(
      getDeviceConfig(deviceData)?.additionalInputs,
    );
    const {
      connectionConfiguration,
      gatewayId,
      enableSmb,
      enableFtp,
      ftpConfiguration: rawFtpConfig,
      smbConfiguration: rawSmbConfig,
    } = connectionData;

    const getFtpConfig = () => {
      if (!rawFtpConfig || !enableFtp) return undefined;
      const { vaultId: _, ...config } = rawFtpConfig;
      return config;
    };

    const getSmbConfig = () => {
      if (!rawSmbConfig || !enableSmb) return undefined;
      const { vaultId: _, ...config } = rawSmbConfig;
      return config;
    };

    const metaData = flattenCredentialMetaData(
      (deviceRest as { metaData?: Record<string, unknown> }).metaData,
      credentialKeys,
    );

    // Picker values outlive a switch to another vendor or type, so only a Soft PLC keeps them.
    const getMetaData = () => {
      if (!isSoftPlcDevice(deviceData) || !softPlc?.autonomyVplcId) {
        return metaData;
      }

      return {
        ...metaData,
        [AUTONOMY_VPLC_ID_META_KEY]: softPlc.autonomyVplcId,
      };
    };

    const device = {
      ...deviceRest,
      metaData: getMetaData(),
      groupId: (deviceGroupId ? deviceGroupId : undefined) ?? groupId,
      connectionConfiguration,
      ftpConfiguration: getFtpConfig(),
      smbConfiguration: getSmbConfig(),
    };

    try {
      const response = await createDevice({ device }).unwrap();

      if (tags) {
        await defineAssetTags({
          tags,
          assetId: response.createDevice.deviceId,
          assetType: AssetTypeEnum.Device,
        });
      }

      if (gatewayId) {
        await linkDevicesToGateway({
          gatewayId,
          deviceId: response.createDevice.deviceId,
        });
      }

      toast.success(t('device:create_success'));
      handleClose?.();

      return response.createDevice;
    } catch {
      toast.error(t('device:create_error'));
    }
  };

  return {
    devicePort,
    boundIpAddress,
    handleDeviceConfiguration,
    handleSubmit,
  };
};
